import React from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import { Query, QueryResult } from 'react-apollo';

import { themes, ReduxState } from 'kb-shared';
import { appointment, freezingCycle } from '../../../assets/icons';
import { DarkButton } from '../../../components/Buttons';
import { History } from 'history';
import { kb360Icon } from 'assets/icons/kb360';
import { isDisneyPatient } from 'kb-shared/utilities/lab.helper';
import { useSelector } from 'react-redux';

const { colors, fonts, breakpoints } = themes;

type PatientResult = QueryResult<{
  patient: {
    id: string;
    patientMembership: {
      id: string;
      employer: boolean;
    };
  };
}>;

interface Action {
  title: string;
  description: string;
  cta: string;
  icon: string;
  path: string;
}

const PATIENT_HAS_MEMBERSHIP_QUERY = gql`
  query patientHasMembership {
    patient {
      id
      patientMembership {
        id
        employer
      }
    }
  }
`;

const actions: Action[] = [
  {
    title: 'Book an appointment',
    description: 'Choose from our full spectrum of services',
    cta: 'Book an appointment',
    icon: appointment,
    path: '/booking-quiz'
  },
  {
    title: 'Cycle Packages',
    description:
      'If you have benefit coverage and have questions about it, please send us a message.',
    cta: 'Purchase Package',
    icon: freezingCycle,
    path: '/cycle-payments'
  },
  {
    title: 'Kindbody360',
    description:
      'Whole-body approach and holistic care support during your family planning experience',
    cta: 'Learn More',
    icon: kb360Icon,
    path: '/kindbody/360'
  }
];

function shouldHide(action: Action, patientHasEmployerMembership: boolean, disneyPatient: boolean) {
  return (
    (action.title === 'Cycle Packages' && patientHasEmployerMembership) ||
    (action.title === 'Kindbody360' && disneyPatient)
  );
}

interface Props {
  history: History;
}

const ActionBanner = ({ history }: Props) => {
  const labId = useSelector((state: ReduxState) => state.patient.patient.lab?.id ?? '');
  const disneyPatient = isDisneyPatient(process.env.REACT_APP_ENV, labId);

  return (
    <Query query={PATIENT_HAS_MEMBERSHIP_QUERY}>
      {({ data }: PatientResult) => {
        const patientHasEmployerMembership =
          (data &&
            data.patient &&
            data.patient.patientMembership &&
            data.patient.patientMembership.employer &&
            data.patient.patientMembership.id !== null) ||
          false;

        return (
          <Container>
            {actions
              .filter(action => !shouldHide(action, patientHasEmployerMembership, disneyPatient)) // hide cycle packages for patients who have a membership and are linked to an employer
              .map(action => (
                <ActionBlock key={action.title}>
                  <img src={action.icon} height="45" width="48" alt="" />
                  <Title>{action.title}</Title>
                  <Description>{action.description}</Description>
                  <DarkButton onClick={() => history.push(action.path)}>{action.cta}</DarkButton>
                </ActionBlock>
              ))}
          </Container>
        );
      }}
    </Query>
  );
};

export default ActionBanner;

// styles
const Container = styled.div`
  display: flex;
  justify-content: space-evenly;
  border: 4px solid ${colors.KBYellow};
  flex-wrap: wrap;
  @media (max-width: ${breakpoints.raw.smallDesktop}px) {
    flex-direction: column;
  }
`;

const ActionBlock = styled.div`
  border-right: 1px solid ${colors.KBNight};
  flex: 1 1 0;
  margin: 20px 0;
  text-align: center;

  @media (max-width: ${breakpoints.raw.smallDesktop}px) {
    border-bottom: 1px solid ${colors.KBNight};
    border-right: none;
    margin: 0 20px;
    padding: 10px 0;
  }

  :last-of-type {
    border: none;
  }
`;

const Title = styled.h3`
  ${fonts.DomaineDisplay};
  font-size: 24px;
  margin-bottom: 0;
`;

const Description = styled.div`
  ${fonts.FoundersGrotesk};
  font-size: 16px;
  margin-bottom: 28px;
`;
